import { useNavigate, useSearchParams } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { Select } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import {
    PERIODOS_DO_PAINEL, avisoDaConta, derivados, objetivoDaCampanha, statusDaCampanha, totaisDoPainel, valorNaMoeda,
} from '@/data/anuncios';
import type { CargaDoPainelDeAnuncios, LinhaDoPainelDeAnuncios } from '@/data/tipos';
import { useConexaoDaMeta } from './ConexaoDaMeta';

const NUMERO = { fontVariantNumeric: 'tabular-nums' } as const;
const APAGADO = { color: 'var(--text-tertiary)' } as const;
const inteiro = (n: number) => n.toLocaleString('pt-BR');

/**
 * Marketing › Anúncios (0162): o que a Meta conta e o que o CRM conta, lado a lado.
 *
 * "Contatos na Meta" é formulário de lead mais conversa iniciada, como a Meta mede.
 * "Leads no CRM" é o lead que a equipe cadastrou e está ligado à campanha — pela
 * conversa que veio do anúncio ou escolhido à mão. Custo por lead e retorno saem do
 * CRM: é dele que a clínica sabe quem virou paciente.
 */
export function PainelDeAnuncios({ painel, aoMudar }: { painel: CargaDoPainelDeAnuncios; aoMudar: () => void }) {
    const [, setParams] = useSearchParams();
    const navigate = useNavigate();
    const { conectar, sincronizar, ocupado, confirmacao } = useConexaoDaMeta(aoMudar);
    const { conta } = painel;
    const moedaDaConta = conta?.moeda || 'BRL';
    const dinheiro = (centavos: number) => valorNaMoeda(centavos, moedaDaConta);

    if (!painel.disponivel) {
        return (
            <Card padding={0}>
                <EmptyState
                    icon="megaphone" title="A integração com a Meta ainda não está ligada"
                    description="A Fórmula MED está configurando o acesso à Meta Ads. Quando estiver pronto, a conexão aparece aqui e em Configurações › Integrações."
                />
            </Card>
        );
    }

    if (!conta || !conta.contaExternaId) {
        return (
            <Card padding={0}>
                <EmptyState
                    icon="plug"
                    title={conta ? 'Falta escolher a conta de anúncios' : 'Conecte a conta de anúncios da clínica'}
                    description={conta
                        ? 'O login na Meta deu certo. Escolha qual conta de anúncios é a da clínica em Configurações › Integrações.'
                        : 'Entre com a conta da Meta que administra os anúncios: investimento, cliques e leads de cada campanha aparecem aqui, cruzados com o CRM. Só leitura.'}
                    action={painel.podeGerir
                        ? conta
                            ? <Button onClick={() => navigate('/app/configuracoes/integracoes?meta-ads=escolher')}>Escolher a conta</Button>
                            : (
                                <Button iconLeft={<Icon name="log-in" size={14} />} loading={ocupado === 'conectar'} onClick={() => { void conectar('/app/marketing/anuncios'); }}>
                                    Conectar Meta Ads
                                </Button>
                            )
                        : undefined}
                />
                {confirmacao}
            </Card>
        );
    }

    const totais = totaisDoPainel(painel.linhas);
    const aviso = avisoDaConta(conta);

    const colunas: DataTableColumn<LinhaDoPainelDeAnuncios>[] = [
        {
            key: 'nome', header: 'Campanha',
            render: (l) => {
                const status = statusDaCampanha(l.status);
                const objetivo = objetivoDaCampanha(l.objetivo);
                return (
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <strong style={{ fontWeight: 600 }}>{l.nome}</strong>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, ...APAGADO }}>
                            <Badge tone={status.tom} dot>{status.rotulo}</Badge>
                            {objetivo}
                            {l.orcamentoDiarioCentavos ? ` · ${dinheiro(l.orcamentoDiarioCentavos)}/dia` : ''}
                        </span>
                    </span>
                );
            },
        },
        { key: 'investidoCentavos', header: 'Investido', align: 'right', width: 120, render: (l) => <span style={NUMERO}>{dinheiro(l.investidoCentavos)}</span> },
        {
            key: 'cliques', header: 'Cliques', align: 'right', width: 120,
            render: (l) => {
                const d = derivados(l);
                return (
                    <span style={NUMERO}>
                        {inteiro(l.cliques)}
                        <span style={{ display: 'block', fontSize: 11.5, ...APAGADO }}>
                            {inteiro(l.impressoes)} impr.{d.ctr !== undefined ? ` · ${d.ctr.toLocaleString('pt-BR')}%` : ''}
                        </span>
                    </span>
                );
            },
        },
        {
            key: 'leadsPlataforma', header: 'Contatos na Meta', align: 'right', width: 130,
            render: (l) => (
                <span style={NUMERO}>
                    {inteiro(l.leadsPlataforma + l.conversasIniciadas)}
                    <span style={{ display: 'block', fontSize: 11.5, ...APAGADO }}>{l.leadsPlataforma} form. · {l.conversasIniciadas} conversas</span>
                </span>
            ),
        },
        {
            key: 'leadsCrm', header: 'Leads no CRM', align: 'right', width: 120,
            render: (l) => <span style={NUMERO}>{l.leadsCrm}<span style={{ display: 'block', fontSize: 11.5, ...APAGADO }}>{l.ganhos} {l.ganhos === 1 ? 'ganho' : 'ganhos'}</span></span>,
        },
        {
            key: 'custo', header: 'Custo por lead', align: 'right', width: 120,
            render: (l) => {
                const d = derivados(l);
                return d.custoPorLeadCentavos === undefined ? <span style={APAGADO}>—</span> : <span style={NUMERO}>{dinheiro(d.custoPorLeadCentavos)}</span>;
            },
        },
        { key: 'receitaCentavos', header: 'Receita', align: 'right', width: 120, render: (l) => <span style={NUMERO}>{dinheiro(l.receitaCentavos)}</span> },
        {
            key: 'retorno', header: 'Retorno', align: 'right', width: 90,
            render: (l) => {
                const d = derivados(l);
                return d.retorno === undefined ? <span style={APAGADO}>—</span> : <strong style={{ fontWeight: 600, ...NUMERO }}>{d.retorno.toLocaleString('pt-BR')}x</strong>;
            },
        },
    ];

    return (
        <>
            <Card padding={14}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, minWidth: 220, fontSize: 13, color: 'var(--text-secondary)' }}>
                        <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{conta.nome}</strong>
                        {' · '}
                        {conta.ultimaSincronizacaoEm ? `lida em ${dataEHoraPorExtenso(conta.ultimaSincronizacaoEm)}` : 'ainda não lida'}
                        {moedaDaConta !== 'BRL' ? ` · valores em ${moedaDaConta}` : ''}
                    </span>
                    <span style={{ width: 170 }}>
                        <Select
                            aria-label="Período" value={String(painel.dias)}
                            onChange={(v) => setParams((p) => { const n = new URLSearchParams(p); n.set('dias', v); return n; }, { replace: true })}
                            options={PERIODOS_DO_PAINEL.map((d) => ({ value: String(d), label: `Últimos ${d} dias` }))}
                        />
                    </span>
                    {painel.podeGerir && (
                        <Button variant="secondary" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} loading={ocupado === 'sincronizar'} onClick={() => { void sincronizar(); }}>
                            Atualizar agora
                        </Button>
                    )}
                </div>
            </Card>

            {aviso && (
                <Alert
                    tone={aviso.tom} title={aviso.titulo} dismissible={false}
                    action={painel.podeGerir && (conta.situacao === 'revogada' || aviso.titulo.startsWith('O login'))
                        ? <Button size="sm" variant="secondary" loading={ocupado === 'conectar'} onClick={() => { void conectar('/app/marketing/anuncios'); }}>Conectar de novo</Button>
                        : undefined}
                >
                    {aviso.texto}
                </Alert>
            )}

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Investido" value={dinheiro(totais.investidoCentavos)} hint={`${inteiro(totais.cliques)} cliques`} icon={<Icon name="wallet" size={16} />} />
                <KpiCard label="Contatos na Meta" value={inteiro(totais.contatosNaPlataforma)} hint="Formulários e conversas" icon={<Icon name="message-circle" size={16} />} />
                <KpiCard label="Leads no CRM" value={inteiro(totais.leadsCrm)} hint={`${totais.ganhos} ${totais.ganhos === 1 ? 'ganho' : 'ganhos'}`} icon={<Icon name="user-plus" size={16} />} />
                <KpiCard label="Custo por lead" value={totais.custoPorLeadCentavos === undefined ? '—' : dinheiro(totais.custoPorLeadCentavos)} hint="Investido ÷ leads no CRM" icon={<Icon name="target" size={16} />} />
                <KpiCard
                    label="Retorno" value={totais.retorno === undefined ? '—' : `${totais.retorno.toLocaleString('pt-BR')}x`}
                    hint={`${dinheiro(totais.receitaCentavos)} em ganhos`} icon={<Icon name="trending-up" size={16} />}
                />
            </div>

            <Card title="Campanhas" subtitle={`De ${painel.de.split('-').reverse().join('/')} a ${painel.ate.split('-').reverse().join('/')}`} padding={0}>
                <DataTable
                    columns={colunas} rows={painel.linhas} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma campanha com gasto ou lead neste período."
                />
            </Card>

            <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-tertiary)' }}>
                O lead entra na campanha sozinho quando a conversa começa por um anúncio de WhatsApp; nos outros casos, escolha a
                campanha no cadastro do lead. A receita é o valor estimado dos leads ganhos, como nas outras campanhas.
            </p>
            {confirmacao}
        </>
    );
}
